import React, { useEffect, useState } from 'react';
import Icon from '../components/ui/Icon';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import './QuestionBank.css';
import { getUser } from '../utils/authUtil';
import QuestionImage from '../components/ui/QuestionImage';
import QuestionText from '../components/ui/QuestionText';

const DIFFICULTY_COLORS = {
  easy:   { bg: '#e5f4ef', color: '#059669' },
  medium: { bg: '#fbf0e0', color: '#d97706' },
  hard:   { bg: '#fdeaea', color: '#dc2626' },
};

const CATEGORY_COLORS = {
  quantitative: '#6d28d9',
  logical:      '#6d28d9',
  verbal:       '#059669',
  programming:  '#dc2626',
  sql:          '#d97706',
};

// Evidence-based health states only (Phase 7 §5) — no arbitrary quality
// score. Colors mirror DIFFICULTY_COLORS' hard/medium palette for severity.
const HEALTH_COLORS = {
  'High error':      { background: 'var(--z-danger-tint)', color: '#dc2626' },
  'Needs review':    { background: 'var(--z-warning-tint)', color: '#d97706' },
  'Frequently used': { background: 'var(--z-tint-indigo)', color: '#4f46e5' },
};

const REVIEW_LABEL = { pending: 'Awaiting platform review', approved: 'In platform bank', rejected: 'Review rejected' };

const emptyForm = {
  question_text: '', topic_id: '', difficulty: 'medium',
  correct_answer: '', explanation: '',
  options: ['', '', '', ''],
  source: 'manual', tags: '',
  company_id: ''
};

const QuestionBank = () => {
  const navigate = useNavigate();
  const [user] = useState(getUser() || {});
  const [questions, setQuestions] = useState([]);
  const [metadata, setMetadata] = useState({ total: 0, page: 1, total_pages: 1 });
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ topic_id: '', difficulty: '', search: '', company_id: '', ownership: '', college_id: '', source: '', status: '', page: 1 });
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkMsg, setBulkMsg] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [allColleges, setAllColleges] = useState([]);
  const [bulkResults, setBulkResults] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);

  const isSuperAdmin = user.role === 'super_admin';

  useEffect(() => {
    fetchTopics();
    fetchCompanies();
    fetchQuestions();
    if (isSuperAdmin) fetchColleges();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => {
  fetchQuestions();
}, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTopics = async () => {
    try {
      const res = await API.get('/questions/topics?includeEmpty=true');
      setTopics(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchCompanies = async () => {
    try {
      const res = await API.get('/questions/companies');
      setCompanies(res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchColleges = async () => {
    try {
      const res = await API.get('/colleges');
      setAllColleges(res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const { status, ...rest } = filters;
      const params = { ...rest, include_health: 'true' };
      if (status === 'flagged') params.flagged = 'true';
      else if (status) params.review_status = status;
      Object.keys(params).forEach(k => { if (params[k] === '') delete params[k]; });
      const res = await API.get('/questions', { params });
      setQuestions(res.data.data);
      setMetadata(res.data.metadata);
      setSelectedIds([]);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };


  const runBulk = async (action) => {
    setBulkMsg('');
    try {
      const res = await API.post('/questions/bulk-action', { action, ids: selectedIds });
      const skipped = res.data.skipped?.length || 0;
      setBulkMsg(`${res.data.updated} updated${skipped ? `, ${skipped} skipped (not yours or not eligible)` : ''}.`);
      fetchQuestions();
    } catch (err) { setBulkMsg(err.response?.data?.message || 'Could not apply that action.'); }
  };

  const submitForReview = async (id) => {
    try {
      await API.post(`/questions/${id}/submit-review`, {});
      setSelectedQuestion(s => (s ? { ...s, review_status: 'pending' } : s));
      fetchQuestions();
    } catch (err) { setBulkMsg(err.response?.data?.message || 'Could not submit this question.'); }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
    setFormSuccess('');
    setShowForm(true);
    setSelectedQuestion(null);
  };

  const openEdit = async (id) => {
    try {
      const res = await API.get(`/questions/${id}`);
      const q = res.data;
      const opts = (q.options || []).length > 0
        ? (typeof q.options[0] === 'string' ? q.options : q.options.map(o => o.option_text || o))
        : ['', '', '', ''];
      setForm({
        question_text: q.question_text,
        topic_id: q.topic_id || '',
        difficulty: q.difficulty || 'medium',
        correct_answer: q.correct_answer || opts[0] || '',
        explanation: q.explanation || '',
        options: opts,
        source: q.source || 'manual',
        tags: q.tags ? q.tags.filter(t => !t.startsWith('Company:')).join(', ') : '',
        company_id: q.company_id || ''
      });
      setEditingId(id);
      setShowForm(true);
      setFormError('');
      setFormSuccess('');
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(''); setFormSuccess('');
    if (!form.question_text.trim()) return setFormError('Question text is required');
    if (!form.topic_id) return setFormError('Please select a topic');
    if (form.options.some(o => !o.trim())) return setFormError('All 4 options are required');
    if (!form.correct_answer) return setFormError('Please select the correct answer');

    try {
      const tagsArray = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];

      const payload = {
        question_text: form.question_text.trim(),
        topic_id: form.topic_id,
        difficulty: form.difficulty,
        correct_answer: form.correct_answer,
        explanation: form.explanation?.trim() || null,
        options: form.options,
        source: form.source || 'manual',
        tags: tagsArray,
        company_id: form.company_id || null
      };
      if (editingId) {
        await API.put(`/questions/${editingId}`, payload);
        setFormSuccess('Question updated successfully!');
      } else {
        await API.post('/questions', payload);
        setFormSuccess('Question added successfully!');
      }
      fetchQuestions();
      setTimeout(() => { setShowForm(false); setForm(emptyForm); }, 1200);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save question');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    setLoading(true);

    try {
      const res = await API.post('/questions/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { message, summary, errors } = res.data;
      setBulkResults({ message, summary, errors: errors || [] });
      setShowBulkModal(true);
      fetchQuestions();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await API.delete(`/questions/${id}`);
      fetchQuestions();
      if (selectedQuestion?.id === id) setSelectedQuestion(null);
    } catch (err) { alert('Failed to delete'); }
  };

  const openPreview = async (id) => {
    try {
      const res = await API.get(`/questions/${id}`);
      setSelectedQuestion(res.data);
      setShowForm(false);
      setUsageStats(null);
      // Best-effort, real usage/accuracy from actual attempt history (Admin
      // Phase 6 §14) — never blocks the preview if it's slow or fails.
      API.get(`/questions/${id}/stats`).then(r => setUsageStats(r.data)).catch(() => {});
    } catch (err) { console.error(err); }
  };

  const groupedByCategory = topics.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  const topicMap = {};
  topics.forEach(t => { topicMap[t.id] = t; });

  const categoryCounts = {};
  questions.forEach(q => {
    const cat = q.category || 'other';
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
  });

  return (
    <>
      <div className="qb-page-content">

        <div className="top-bar">
  <div>
    <h1>Question Bank</h1>
    <p>{metadata.total} question{metadata.total === 1 ? "" : "s"}{metadata.total_pages > 1 ? ` · page ${metadata.page} of ${metadata.total_pages}` : ""}</p>
  </div>
  <div style={{ display: 'flex', gap: 10 }}>
    <button className="btn-bulk" onClick={() => document.getElementById('bulkInput').click()}>
      <Icon name="plus" size={16} /> Bulk Upload
    </button>
    <input id="bulkInput" type="file" accept=".xlsx,.xls,.csv"
      style={{ display: 'none' }}
      onChange={handleBulkUpload}
    />
    <button className="btn-add" onClick={openAdd}>+ Add Question</button>
    <button className="btn-bulk" onClick={() => navigate('/admin/topics')}>
      <Icon name="book" size={16} /> Manage Topics
    </button>
  </div>
</div>


        <div className="qb-filters">
          <input className="qb-search" placeholder="Search questions..."
            value={filters.search}
            onChange={e => setFilters(p => ({ ...p, search: e.target.value }))} />

          <select className="qb-select" value={filters.topic_id}
            onChange={e => setFilters(p => ({ ...p, topic_id: e.target.value }))}>
            <option value="">All Topics</option>
            {Object.entries(groupedByCategory).map(([cat, tlist]) => (
              <optgroup key={cat} label={cat.toUpperCase()}>
                {tlist.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </optgroup>
            ))}
          </select>

          <select className="qb-select" value={filters.difficulty}
            onChange={e => setFilters({ ...filters, difficulty: e.target.value, page: 1 })}>
            <option value="">All Difficulty</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>

          <select className="qb-select" value={filters.company_id}
            onChange={e => setFilters({ ...filters, company_id: e.target.value, page: 1 })}>
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <select className="qb-select" aria-label="Ownership" value={filters.ownership}
            onChange={e => setFilters(p => ({ ...p, ownership: e.target.value, page: 1 }))}>
            <option value="">All questions</option>
            <option value="global">Platform bank</option>
            <option value="college">{isSuperAdmin ? 'College questions' : 'Our college'}</option>
          </select>

          <select className="qb-select" aria-label="Source" value={filters.source}
            onChange={e => setFilters(p => ({ ...p, source: e.target.value, page: 1 }))}>
            <option value="">Any source</option>
            <option value="manual">Manual</option>
            <option value="imported">Imported</option>
            <option value="generated">Generated</option>
          </select>

          <select className="qb-select" aria-label="Status" value={filters.status}
            onChange={e => setFilters(p => ({ ...p, status: e.target.value, page: 1 }))}>
            <option value="">Any status</option>
            <option value="flagged">Flagged</option>
            <option value="pending">Awaiting platform review</option>
            <option value="approved">In platform bank</option>
            <option value="rejected">Review rejected</option>
          </select>

          {isSuperAdmin && (
            <select className="qb-select" value={filters.college_id}
              onChange={e => setFilters(p => ({ ...p, college_id: e.target.value }))}>
              <option value="">All Colleges</option>
              {allColleges.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}

          {(filters.topic_id || filters.difficulty || filters.search || filters.ownership || filters.college_id || filters.source || filters.status) && (
            <button className="clear-btn"
              onClick={() => setFilters({ topic_id: '', difficulty: '', search: '', company_id: '', ownership: '', college_id: '', source: '', status: '', page: 1 })}>
              Clear
            </button>
          )}
        </div>

        <div className="qb-layout">
          {/* Question list */}
          <div className="qb-list">
            {questions.length > 0 && (
              <div className="qb-bulk" role="toolbar" aria-label="Bulk actions">
                <label className="qb-bulk-all">
                  <input type="checkbox" checked={selectedIds.length === questions.length} onChange={e => setSelectedIds(e.target.checked ? questions.map(q => q.id) : [])} />
                  {selectedIds.length ? `${selectedIds.length} selected` : 'Select page'}
                </label>
                {selectedIds.length > 0 && (
                  <>
                    <button type="button" onClick={() => runBulk('flag')}>Flag</button>
                    <button type="button" onClick={() => runBulk('unflag')}>Unflag</button>
                    {!isSuperAdmin && <button type="button" onClick={() => runBulk('submit_review')}>Submit for platform review</button>}
                  </>
                )}
                {bulkMsg && <span className="qb-bulk-msg" role="status">{bulkMsg}</span>}
              </div>
            )}
            {loading ? (
              <div className="loading">Loading questions...</div>
            ) : questions.length === 0 ? (
              <div className="qb-empty">
                <div className="qb-empty-icon">?</div>
                <p>No questions yet</p>
                <p className="qb-empty-sub">Click "+ Add Question" to add your first question</p>
              </div>
            ) : (
              questions.map((q, i) => {
                const catColor = CATEGORY_COLORS[q.category] || '#e2e8f0';
                const diff = DIFFICULTY_COLORS[q.difficulty] || DIFFICULTY_COLORS.medium;
                return (
                  <div key={q.id} className={`qb-item ${selectedQuestion?.id === q.id ? 'active' : ''}`}
                    onClick={() => openPreview(q.id)}
                    role="button" tabIndex={0}
                    aria-label={`Preview question ${i + 1}: ${q.question_text}`}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPreview(q.id); } }}>
                    <div className="qb-item-top">
                      <input type="checkbox" className="qb-check" aria-label={`Select question ${i + 1}`} checked={selectedIds.includes(q.id)}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setSelectedIds(p => (e.target.checked ? [...p, q.id] : p.filter(x => x !== q.id)))} />
                      <div className="qb-num">{(metadata.page - 1) * (metadata.limit || 20) + i + 1}</div>
                      <div className="qb-item-body">
                        <div className="qb-question-text">{q.question_text}</div>
                        <div className="qb-item-meta">
                          <span className="qb-topic" style={{ background: catColor + '18', color: catColor }}>
                            {q.topic_name || 'No topic'}
                          </span>
                          <span className="qb-diff" style={{ background: diff.bg, color: diff.color }}>
                            {q.difficulty}
                          </span>
                          {q.is_global ? (
                            <span className="qb-ownership global" title="Global Pool"><Icon name="building" size={13} /> Global Pool</span>
                          ) : (
                            <span className="qb-ownership college" title="College Specific"><Icon name="building" size={13} /> College Specific</span>
                          )}
                          {q.company_name && (
                            <span className="qb-company"><Icon name="placement" size={13} /> {q.company_name}</span>
                          )}
                          {q.image_path && (
                            <span className="qb-source" title="This question includes a chart"><Icon name="chart" size={13} /> Chart</span>
                          )}
                          {q.health && q.health.available && q.health.health !== 'Healthy' && (
                            <span className="qb-health" style={HEALTH_COLORS[q.health.health] || {}} title={q.health.signals.join(', ')}>
                              <Icon name="warning" size={13} /> {q.health.health}
                            </span>
                          )}
                          <span className="qb-source"><Icon name="profile" size={13} /> {q.added_by_name}</span>
                          <span className="qb-source" title="Assessments that include this question"><Icon name="test" size={13} /> Used in {q.used_in_tests ?? 0}</span>
                          {q.health?.available && <span className="qb-source" title="Accuracy across all attempts"><Icon name="check" size={13} /> {q.health.accuracy_pct}% correct</span>}
                          {q.is_flagged && <span className="qb-health" style={{ background: 'var(--z-warning-tint)', color: 'var(--z-warning)' }}><Icon name="flag" size={13} /> Flagged</span>}
                          {q.review_status && <span className="qb-source">{REVIEW_LABEL[q.review_status]}</span>}
                        </div>
                      </div>
                      <div className="qb-item-actions" onClick={e => e.stopPropagation()}>
                        {(isSuperAdmin || q.college_id === user.college_id) && (
                          <>
                            <button className="btn-edit-sm" onClick={() => openEdit(q.id)}>Edit</button>
                            <button className="btn-del-sm"  onClick={() => handleDelete(q.id)}>Del</button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {/* Pagination Controls */}
            {metadata.total_pages > 1 && (
              <div className="qb-pagination">
                <button 
                  disabled={filters.page <= 1}
                  onClick={() => setFilters(p => ({ ...p, page: p.page - 1 }))}
                  className="page-btn"
                >
                  <Icon name="chevron-left" size={16} /> Previous
                </button>
                <span className="page-info">
                  Page {metadata.page} of {metadata.total_pages}
                </span>
                <button 
                  disabled={filters.page >= metadata.total_pages}
                  onClick={() => setFilters(p => ({ ...p, page: p.page + 1 }))}
                  className="page-btn"
                >
                  Next <Icon name="chevron-right" size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Right panel — Add/Edit form OR Preview */}
          <div className="qb-right">

            {showForm && (
              <div className="qb-form-card">
                <div className="qb-form-header">
                  <h3>{editingId ? 'Edit Question' : 'Add New Question'}</h3>
                  <button className="icon-close" onClick={() => setShowForm(false)}>×</button>
                </div>

                {formError   && <div className="alert error">{formError}</div>}
                {formSuccess && <div className="alert success">{formSuccess}</div>}

                <form onSubmit={handleSubmit}>
                  <div className="fg">
                    <label>Question Text *</label>
                    <textarea rows={3} placeholder="Enter the question..."
                      value={form.question_text}
                      onChange={e => setForm(p => ({ ...p, question_text: e.target.value }))} />
                  </div>

                  <div className="form-row">
                    <div className="fg">
                      <label>Topic *</label>
                      <select value={form.topic_id}
                        onChange={e => setForm(p => ({ ...p, topic_id: e.target.value }))}>
                        <option value="">Select topic</option>
                        {Object.entries(groupedByCategory).map(([cat, tlist]) => (
                          <optgroup key={cat} label={cat.toUpperCase()}>
                            {tlist.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div className="fg">
                      <label>Difficulty *</label>
                      <select value={form.difficulty}
                        onChange={e => setForm(p => ({ ...p, difficulty: e.target.value }))}>
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="fg">
                      <label>Source</label>
                      <select value={form.source}
                        onChange={e => setForm(p => ({ ...p, source: e.target.value }))}>
                        <option value="manual">Manual (Admin Created)</option>
                        <option value="generated">AI Generated</option>
                      </select>
                    </div>
                  </div>

                  <div className="fg">
                    <label>Tags (comma separated)</label>
                    <input type="text" placeholder="Logic, Arithmetic, 2024..."
                      value={form.tags}
                      onChange={e => setForm({ ...form, tags: e.target.value })} />
                  </div>

                  <div className="fg">
                    <label>Target Company (Optional)</label>
                    <select value={form.company_id}
                      onChange={e => setForm({...form, company_id: e.target.value})}>
                      <option value="">No Specific Company</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>

                  <div className="fg">
                    <label>Options (A, B, C, D) * — Click letter to mark as correct</label>
                    {form.options.map((opt, i) => (
                      <div key={i} className="option-row">
                        <div
                          className={`opt-label ${form.correct_answer === opt && opt ? 'correct' : ''}`}
                          onClick={() => opt.trim() && setForm(p => ({ ...p, correct_answer: opt }))}
                          title="Click to mark as correct answer">
                          {String.fromCharCode(65 + i)}
                        </div>
                        <input placeholder={`Option ${String.fromCharCode(65 + i)}`}
                          value={opt}
                          onChange={e => {
                            const opts = [...form.options];
                            // If this option was the correct answer, update its text
                            const wasCorrect = form.correct_answer === opts[i];
                            opts[i] = e.target.value;
                            setForm(p => ({
                              ...p,
                              options: opts,
                              correct_answer: wasCorrect ? e.target.value : p.correct_answer
                            }));
                          }} />
                        {form.correct_answer === opt && opt && (
                          <span className="correct-tick"><Icon name="check" size={13} /> Correct</span>
                        )}
                      </div>
                    ))}
                    <div className="opt-hint">Click the letter (A/B/C/D) to mark as the correct answer</div>
                    {form.correct_answer && (
                      <div style={{ fontSize: 12, color: '#059669', marginTop: 4 }}><Icon name="check" size={13} /> Correct answer set: "{form.correct_answer}"</div>
                    )}
                  </div>

                  <div className="fg">
                    <label>Explanation (optional)</label>
                    <textarea rows={2} placeholder="Solution walkthrough..."
                      value={form.explanation}
                      onChange={e => setForm(p => ({ ...p, explanation: e.target.value }))} />
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-cancel"
                      onClick={() => { setShowForm(false); setForm(emptyForm); }}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-submit">
                      {editingId ? 'Update Question' : 'Add Question'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {selectedQuestion && !showForm && (
              <div className="qb-preview-card">
                <div className="qb-form-header">
                  <h3>Question Preview</h3>
                  <button className="icon-close" onClick={() => setSelectedQuestion(null)}>×</button>
                </div>

                <div className="preview-meta">
                  <span className="qb-topic"
                    style={{
                      background: (CATEGORY_COLORS[selectedQuestion.category] || '#64748b') + '18',
                      color: CATEGORY_COLORS[selectedQuestion.category] || '#64748b'
                    }}>
                    {selectedQuestion.topic_name || 'No topic'}
                  </span>
                  <span className="qb-diff"
                    style={{
                      background: (DIFFICULTY_COLORS[selectedQuestion.difficulty] || DIFFICULTY_COLORS.easy).bg,
                      color: (DIFFICULTY_COLORS[selectedQuestion.difficulty] || DIFFICULTY_COLORS.easy).color
                    }}>
                    {selectedQuestion.difficulty}
                  </span>
                  {selectedQuestion.is_global ? (
                    <span className="qb-ownership global" title="Global Pool"><Icon name="building" size={13} /> Global Pool</span>
                  ) : (
                    <span className="qb-ownership college" title={selectedQuestion.added_by_college}><Icon name="building" size={13} /> {selectedQuestion.added_by_college}</span>
                  )}
                  {selectedQuestion.company_name && (
                    <span className="qb-company"><Icon name="placement" size={13} /> {selectedQuestion.company_name}</span>
                  )}
                  <span className="qb-source" title="Source"><Icon name="list" size={13} /> {selectedQuestion.source || 'manual'}</span>
                  <span className="qb-source"><Icon name="profile" size={13} /> Added by {selectedQuestion.added_by_name}</span>
                </div>

                <div className="preview-question"><QuestionText text={selectedQuestion.question_text} /></div>
                <QuestionImage path={selectedQuestion.image_path} />

                <div className="preview-options">
                  {selectedQuestion.options?.map((opt, i) => {
                    const optText = typeof opt === 'string' ? opt : opt.option_text;
                    const isCorrect = optText === selectedQuestion.correct_answer;
                    return (
                      <div key={i} className={`preview-option ${isCorrect ? 'correct' : ''}`}>
                        <span className="preview-opt-letter">{String.fromCharCode(65 + i)}</span>
                        <span>{optText}</span>
                        {isCorrect && <span className="preview-correct-badge"><Icon name="check" size={13} /> Correct</span>}
                      </div>
                    );
                  })}
                </div>

                {selectedQuestion.explanation && (
                  <div className="preview-explanation">
                    <div className="exp-label">Explanation</div>
                    <div className="exp-text">{selectedQuestion.explanation}</div>
                  </div>
                )}

                {usageStats && (
                  <div className="preview-explanation" style={{ background: usageStats.available && usageStats.accuracy_pct < 30 ? '#fdeaea' : '#f5f4fb' }}>
                    <div className="exp-label">Real Usage</div>
                    {usageStats.available ? (
                      <div className="exp-text">
                        {usageStats.attempts} attempts · {usageStats.accuracy_pct}% accuracy · {usageStats.skip_rate_pct}% skipped
                        <br /><strong>{usageStats.signals.join(', ')}</strong>
                      </div>
                    ) : (
                      <div className="exp-text">{usageStats.message}</div>
                    )}
                  </div>
                )}

                {selectedQuestion.review_status && (
                  <div className="exp-text">
                    <strong>{REVIEW_LABEL[selectedQuestion.review_status]}</strong>
                    {selectedQuestion.review_note ? ` — ${selectedQuestion.review_note}` : ''}
                  </div>
                )}
                <div className="preview-actions">
                  {!isSuperAdmin && selectedQuestion.college_id === user.college_id && (!selectedQuestion.review_status || selectedQuestion.review_status === 'rejected') && (
                    <button className="btn-edit-full" onClick={() => submitForReview(selectedQuestion.id)}>Submit for platform review</button>
                  )}
                  <button className="btn-edit-full" onClick={() => openEdit(selectedQuestion.id)}>
                    Edit Question
                  </button>
                  <button className="btn-del-full" onClick={() => handleDelete(selectedQuestion.id)}>
                    Delete
                  </button>
                </div>
              </div>
            )}

            {!showForm && !selectedQuestion && (
              <div className="qb-right-empty">
                <div style={{ fontSize: 40, marginBottom: 12, color: '#94a3b8' }}>?</div>
                <p>Select a question to preview</p>
                <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>or click "+ Add Question"</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showBulkModal && bulkResults && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-box bulk-results-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bulk Upload Results</h3>
              <button className="modal-close" onClick={() => setShowBulkModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="bulk-summary">
                <div className="summary-card added">
                  <div className="summary-val">{bulkResults.summary?.added || 0}</div>
                  <div className="summary-label">Added</div>
                </div>
                <div className="summary-card skipped">
                  <div className="summary-val">{bulkResults.summary?.skipped || 0}</div>
                  <div className="summary-label">Skipped</div>
                </div>
                {(bulkResults.summary?.duplicates || 0) > 0 && (
                  <div className="summary-card skipped" title="Already in the bank — never overwritten">
                    <div className="summary-val">{bulkResults.summary.duplicates}</div>
                    <div className="summary-label">Already exist</div>
                  </div>
                )}
                <div className="summary-card total">
                  <div className="summary-val">{bulkResults.summary?.total || 0}</div>
                  <div className="summary-label">Total processed</div>
                </div>
              </div>

              {bulkResults.errors && bulkResults.errors.length > 0 && (
                <div className="bulk-errors">
                  <h4>Errors ({bulkResults.errors.length})</h4>
                  <div className="error-list">
                    {bulkResults.errors.map((err, idx) => (
                      <div key={idx} className="error-item">
                        <span className="err-row">Row {err.row}:</span>
                        <span className="err-msg">{err.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-submit" onClick={() => setShowBulkModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuestionBank;